import { ExternalToast, toast as sonnerToast } from "sonner";

import type React from "react";

const recentAlertToasts = new Set<string>();
const ALERT_COOLDOWN_MS = 5000;

function playAlertSound(variant: "error" | "warning", toastId?: string) {
  // If this toast has a stable ID, only play the sound once per cooldown period
  if (toastId) {
    if (recentAlertToasts.has(toastId)) return;
    recentAlertToasts.add(toastId);
    setTimeout(() => recentAlertToasts.delete(toastId), ALERT_COOLDOWN_MS);
  }

  try {
    const ctx = new AudioContext();
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();

    oscillator.connect(gain);
    gain.connect(ctx.destination);

    // Error: two descending tones; Warning: single tone
    if (variant === "error") {
      oscillator.frequency.setValueAtTime(880, ctx.currentTime);
      oscillator.frequency.setValueAtTime(440, ctx.currentTime + 0.15);
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.4);
    } else {
      oscillator.frequency.setValueAtTime(660, ctx.currentTime);
      gain.gain.setValueAtTime(0.2, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.3);
    }

    oscillator.onended = () => ctx.close();
  } catch {
    // Audio not available — silently ignore
  }
}

interface ToastActionElement {
  label: string;
  altText: string;
  onClick: () => void | Promise<void>;
}

interface ToastProps {
  variant?: "info" | "success" | "warning" | "error" | "loading";
  title?: string;
  description?: string | React.ReactNode;
  action?: ToastActionElement;
  disableDismiss?: boolean;
  playSound?: boolean;
}

type ToasterToast = ToastProps & {
  id?: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: ToastActionElement;
} & ExternalToast;

function toast({
  title,
  description,
  variant,
  action,
  id,
  playSound,
  ...props
}: ToasterToast) {
  // Pass ReactNode title/description through unchanged — sonner accepts both.
  // String() coercion here would render a JSX element as "[object Object]".
  const titleHasContent = title !== undefined && title !== null && title !== "";
  const descHasContent =
    description !== undefined && description !== null && description !== "";
  const message: string | React.ReactNode = titleHasContent
    ? (title as string | React.ReactNode)
    : descHasContent
      ? (description as string | React.ReactNode)
      : "Notification";
  const toastDescription =
    titleHasContent && descHasContent
      ? (description as string | React.ReactNode)
      : undefined;

  const sonnerOptions = {
    description: toastDescription,
    action: action
      ? {
          label: action.label,
          onClick: action.onClick,
        }
      : undefined,
    id: id,
    ...props,
  };

  let toastId: string | number;

  switch (variant) {
    case "success":
      toastId = sonnerToast.success(message, sonnerOptions);
      break;
    case "error":
      if (playSound) playAlertSound("error", id);
      toastId = sonnerToast.error(message, sonnerOptions);
      break;
    case "warning":
      if (playSound) playAlertSound("warning", id);
      toastId = sonnerToast.warning(message, sonnerOptions);
      break;
    case "loading":
      toastId = sonnerToast.loading(message, sonnerOptions);
      break;
    case "info":
    default:
      toastId = sonnerToast.info(message, sonnerOptions);
      break;
  }

  return {
    id: toastId.toString(),
    dismiss: () => sonnerToast.dismiss(toastId),
    update: (newProps: ToasterToast) => {
      toast({ ...newProps, id: toastId.toString() });
    },
  };
}

function useToast() {
  return {
    toasts: [],
    toast,
    dismiss: (toastId?: string) => {
      if (toastId) {
        sonnerToast.dismiss(toastId);
      } else {
        sonnerToast.dismiss();
      }
    },
  };
}

export { toast, useToast, sonnerToast };
